import React, { useCallback, useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import ToolbarItem from './ToolbarItem';
import ToolbarGroupItem from './ToolbarGroupItem';

import ID from './UUID';
import store from './stores/store';
import { groupBy } from './functions';
import { ToolbarContainer } from './FieldLimitAlert/FieldLimitComponents';

function buildItems(items, defaultItems) {
	if (!items) {
		return defaultItems;
	}
	return items.map((x) => {
		let found = defaultItems.find((y) => x.element === y.element && y.key === x.key);
		if (!found) {
			found = defaultItems.find((y) => (x.element || x.key) === (y.element || y.key));
		}
		if (found) {
			if (x.inherited !== false) {
				found = { ...found, ...x };
			} else if (x.group_name) {
				found.group_name = x.group_name;
			}
		}
		return found || x;
	});
}

function buildGroupItems(allItems) {
	const items = allItems.filter((x) => !x.group_name);
	const gItems = allItems.filter((x) => !!x.group_name);
	const grouped = groupBy(gItems, (x) => x.group_name);
	const groupKeys = gItems.map((x) => x.group_name).filter((v, i, self) => self.indexOf(v) === i);
	return { items, grouped, groupKeys };
}

// Move _defaultItems outside component to avoid circular dependency issues
function getDefaultItems(intl) {
	return [
		{
			key: 'Header',
			name: intl.formatMessage({ id: 'header-text' }),
			icon: 'fas fa-heading',
			static: true,
			content: intl.formatMessage({ id: 'place-holder-text' })
		},
		{
			key: 'Label',
			name: intl.formatMessage({ id: 'label' }),
			static: true,
			icon: 'fas fa-font',
			content: intl.formatMessage({ id: 'place-holder-text' })
		},
		{
			key: 'Paragraph',
			name: intl.formatMessage({ id: 'paragraph' }),
			static: true,
			icon: 'fas fa-paragraph',
			content: intl.formatMessage({ id: 'place-holder-text' })
		},
		{
			key: 'LineBreak',
			name: intl.formatMessage({ id: 'line-break' }),
			static: true,
			icon: 'fas fa-arrows-alt-h'
		},

		{
			key: 'RadioButtons',
			canHaveAnswer: true,
			name: intl.formatMessage({ id: 'multiple-choice' }),
			icon: 'far fa-dot-circle',
			label: intl.formatMessage({ id: 'place-holder-label' }),
			field_name: 'radiobuttons_',
			options: []
		},
		{
			key: 'TextInput',
			canHaveAnswer: true,
			name: intl.formatMessage({ id: 'text-input' }),
			label: intl.formatMessage({ id: 'place-holder-label' }),
			icon: 'fas fa-font',
			field_name: 'text_input_'
		},

		{
			key: 'TextArea',
			canHaveAnswer: true,
			name: intl.formatMessage({ id: 'multi-line-input' }),
			label: intl.formatMessage({ id: 'place-holder-label' }),
			icon: 'fas fa-text-height',
			field_name: 'text_area_'
		},
		{
			key: 'TwoColumnRow',
			canHaveAnswer: false,
			name: intl.formatMessage({ id: 'two-columns-row' }),
			label: '',
			icon: 'fas fa-columns',
			field_name: 'two_col_row_'
		},
		{
			key: 'ThreeColumnRow',
			canHaveAnswer: false,
			name: intl.formatMessage({ id: 'three-columns-row' }),
			label: '',
			icon: 'fas fa-columns',
			field_name: 'three_col_row_'
		},
		{
			key: 'FourColumnRow',
			element: 'MultiColumnRow',
			canHaveAnswer: false,
			name: intl.formatMessage({ id: 'four-columns-row' }),
			label: '',
			icon: 'fas fa-columns',
			field_name: 'four_col_row_',
			col_count: 4,
			class_name: 'col-md-3'
		},

		{
			key: 'Image',
			name: intl.formatMessage({ id: 'image' }),
			label: '',
			icon: 'far fa-image',
			field_name: 'image_',
			src: ''
		},

		{
			key: 'Signature',
			canread_only: true,
			name: intl.formatMessage({ id: 'signature' }),
			icon: 'fas fa-pen-square',
			label: intl.formatMessage({ id: 'signature' }),
			field_name: 'signature_'
		},
		{
			key: 'HyperLink',
			name: intl.formatMessage({ id: 'website' }),
			icon: 'fas fa-link',
			static: true,
			content: intl.formatMessage({ id: 'place-holder-website-link' }),
			href: 'http://www.example.com'
		},
		{
			key: 'Download',
			name: intl.formatMessage({ id: 'file-attachment' }),
			icon: 'fas fa-file',
			static: true,
			content: intl.formatMessage({ id: 'place-holder-file-name' }),
			field_name: 'download_',
			file_path: '',
			_href: ''
		},
		{
			key: 'FileUpload',
			name: intl.formatMessage({ id: 'file-upload' }),
			icon: 'fas fa-file',
			label: intl.formatMessage({ id: 'place-holder-label' }),
			field_name: 'file_upload_'
		}
	];
}

const Toolbar = ({ intl, showDescription, enableAddOnClick, items: itemsProps, disabled }) => {
	const _defaultItemOptions = useCallback((element, intl) => {
		switch (element) {
			case 'MUI_Dropdown':
			case 'MUI_RadioButton':
			case 'MUI_Checkbox':
				return [
					{
						value: '1',
						text: 'Option 1',
						key: `option_${ID.uuid()}`
					},
					{
						value: '2',
						text: 'Option 2',
						key: `option_${ID.uuid()}`
					}
				];
			case 'MUI_Tags':
				return [
					{
						value: 'one',
						text: 'Option 1',
						key: `_option_${ID.uuid()}`
					},
					{
						value: 'two',
						text: 'Option 2',
						key: `_option_${ID.uuid()}`
					}
				];

			default:
				return [];
		}
	}, []);

	// State initialization with error handling
	const [items] = useState(() => {
		try {
			return buildItems(itemsProps, getDefaultItems(intl));
		} catch (error) {
			console.error('Error initializing toolbar items:', error);
			return [];
		}
	});
	const [, setStoreState] = useState(null);

	useEffect(() => {
		const unsubscribe = store.subscribe((state) => {
			setStoreState(state);
		});

		return () => {
			if (typeof unsubscribe === 'function') {
				unsubscribe();
			}
		};
	}, []);

	const create = useCallback(
		(item) => {
			const elementOptions = {
				id: ID.uuid(),
				element: item.element || item.key,
				text: item.name,
				group_name: item.group_name,
				static: item.static,
				required: false,
				showDescription: item.showDescription
			};

			if (showDescription === true && !item.static) {
				elementOptions.showDescription = true;
			}

			if (item.type === 'custom') {
				elementOptions.key = item.key;
				elementOptions.custom = true;
				elementOptions.forward_ref = item.forward_ref;
				elementOptions.bare = item.bare;
				elementOptions.props = item.props;
				elementOptions.component = item.component || null;
				elementOptions.custom_options = item.custom_options || [];
			}

			if (item.static) {
				elementOptions.bold = false;
				elementOptions.italic = false;
			}

			if (item.canHaveAnswer) {
				elementOptions.canHaveAnswer = item.canHaveAnswer;
			}

			if (item.canread_only) {
				elementOptions.read_only = false;
			}

			if (item.canDefaultToday) {
				elementOptions.defaultToday = false;
			}

			if (item.content) {
				elementOptions.content = item.content;
			}

			if (item.href) {
				elementOptions.href = item.href;
			}

			if (item.inherited !== undefined) {
				elementOptions.inherited = item.inherited;
			}

			elementOptions.can_have_page_break_before = item.can_have_page_break_before !== false;
			elementOptions.can_have_alternate_form = item.can_have_alternate_form !== false;
			elementOptions.can_have_display_horizontal = item.can_have_display_horizontal !== false;
			if (elementOptions.can_have_display_horizontal) {
				elementOptions.inline = item.inline;
			}
			elementOptions.can_have_option_correct = item.can_have_option_correct !== false;
			elementOptions.can_have_option_value = item.can_have_option_value !== false;
			elementOptions.can_populate_from_api = item.can_populate_from_api !== false;

			if (item.class_name) {
				elementOptions.class_name = item.class_name;
			}

			if (item.key === 'Image') {
				elementOptions.src = item.src;
			}

			if (item.key === 'DatePicker') {
				elementOptions.dateFormat = item.dateFormat;
				elementOptions.timeFormat = item.timeFormat;
				elementOptions.showTimeSelect = item.showTimeSelect;
				elementOptions.showTimeSelectOnly = item.showTimeSelectOnly;
				elementOptions.showTimeInput = item.showTimeInput;
			}

			if (item.key === 'Download') {
				elementOptions._href = item._href;
				elementOptions.file_path = item.file_path;
			}

			if (item.key === 'Range') {
				elementOptions.step = item.step;
				elementOptions.default_value = item.default_value;
				elementOptions.min_value = item.min_value;
				elementOptions.max_value = item.max_value;
				elementOptions.min_label = item.min_label;
				elementOptions.max_label = item.max_label;
			}

			if (item.element === 'MultiColumnRow') {
				elementOptions.col_count = item.col_count;
			}

			if (item.defaultValue) {
				elementOptions.defaultValue = item.defaultValue;
			}

			if (item.field_name) {
				elementOptions.field_name = item.field_name + ID.uuid();
			}

			if (item.label) {
				elementOptions.label = item.label;
			}

			if (item.second_label) {
				elementOptions.second_label = item.second_label;
			}

			if (item.options) {
				if (item.options.length > 0) {
					if (['MUI_RadioButton', 'MUI_Checkbox', 'MUI_Dropdown', 'MUI_Tags'].includes(item.key)) {
						elementOptions.options = _defaultItemOptions(item.key, intl);
					} else {
						elementOptions.options = item.options;
					}
				} else {
					elementOptions.options = _defaultItemOptions(elementOptions.element, intl);
				}
			}

			return elementOptions;
		},
		[intl, _defaultItemOptions]
	);

	const _onClick = useCallback(
		(item) => {
			if (disabled) {
				return;
			}

			store.dispatch('create', create(item));
		},
		[create, disabled]
	);

	const renderItem = useCallback(
		(item) => (
			<ToolbarItem
				data={item}
				key={item.key}
				onClick={enableAddOnClick && !disabled && _onClick.bind(null, item)}
				onCreate={create}
				disabled={disabled}
			/>
		),
		[enableAddOnClick, _onClick, create, disabled]
	);

	const { items: processedItems, grouped, groupKeys } = buildGroupItems(items);

	return (
		<ToolbarContainer
			id="react-form-builder-toolbar"
			className="react-form-builder-toolbar"
			disabled={disabled}
		>
			<ul>
				{processedItems.map(renderItem)}
				{groupKeys.map((k, index) => (
					<ToolbarGroupItem
						data-testid={`formbuilder-toolbargroup-item-${index}`}
						key={k}
						name={k}
						group={grouped.get(k)}
						renderItem={renderItem}
						disabled={disabled}
					/>
				))}
			</ul>
		</ToolbarContainer>
	);
};

export default injectIntl(Toolbar);
